import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Database } from "@phosphor-icons/react";

export default function RecordsExplorer() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/records/search", { params: { q: q || undefined, status } });
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, [status]);

  return (
    <Layout>
      <PageHeader title="Records Explorer" subtitle="Trace any source record to its destination ID & status" />
      <div className="p-8 space-y-4">
        <div className="flex gap-2 max-w-2xl">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="records-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Search by source ID or target ID…" className="pl-9 rounded-xl" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="records-status" className="rounded-xl w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "success", "failed", "skipped"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="records-search-btn" onClick={search} className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200">Search</Button>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                <th className="px-6 py-3 font-semibold">Source ID</th>
                <th className="px-6 py-3 font-semibold">Target ID</th>
                <th className="px-6 py-3 font-semibold">Object</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Message</th>
                <th className="px-6 py-3 font-semibold text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors duration-200">
                  <td className="px-6 py-3 font-mono text-xs">{r.source_id}</td>
                  <td className="px-6 py-3 font-mono text-xs text-[#C2410C]">{r.target_id || "—"}</td>
                  <td className="px-6 py-3 text-xs">{r.object_mapping}</td>
                  <td className="px-6 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-6 py-3 text-xs text-stone-500 max-w-md truncate">{r.error || ""}</td>
                  <td className="px-6 py-3 text-right text-xs text-stone-400">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-stone-400">
                  <Database size={32} className="mx-auto mb-3 text-stone-300" />No records found.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
